import { ColumnProps } from 'primevue/column';
import { AppEnums } from '../../shared/enums';
import { RouteLocationRaw } from 'vue-router';
import type { MenuItem } from 'primevue/menuitem';
import { AppCoreEnums } from 'src/core/shared/enums';
import { ToastMessageOptions } from 'primevue/toast';
import { EntityStatuses } from '@/shared/constants';
import { IFormSavePromise, ResolveDrawerParams } from 'src/core/models/routing';
import { Ref } from 'vue';

export interface ICoreState {
  useMiniForMainDrawer: boolean;
  useSilentApiCall: boolean;
  pendingRequests: string[];

  toastMessage: IToastMessage | undefined;
  collapsibleTableContainerData: CollapsibleTableContainerData;
  collapsibleTableInnerContainerData: CollapsibleTableInnerContainerData;
  tabsContainerData: TabsContainerData;
  tableContainerData: TableContainerData;
  tableInnerContainerData: TableInnerContainerData;
  collapsibleContainerData: CollapsibleContainerData;
  formContainerData: FormContainerData;
  sizes: SizeInfo;
  confirmationDialogData: ConfirmationDialogData;
  drawerContainerData: DrawerContainerData;
  listContainerData: ListContainerData;
  breadcrumbItems: BreadcrumbItem[];
  resetBreadcrumbs: boolean;
  activeContextMenuItem: AppMainMenuItem | undefined;

  //
  // Right Side Drawer
  //
  drawerInfo: DrawerInfo | undefined;
  //rightSideDrawerPagination: Pagination;

  //
  // Logs Table Container
  //
  logsTableContainerSearchBy: string;
  logsTableContainerPagination: Pagination;
  logsTableContainerSelectedRowKey: string | undefined;
  logsTableContainerFilters: IFilterItem[];

  deleteResult: DeleteResult;
}

export interface UserPreference {
  preferenceKey: string;
  preferenceValue: string;
}

export interface UiMenuItem {
  id: string;
  parentId: string;
  title: string;
  caption: string;
  icon: string;
  route: string;
}

export interface IErrorResult {
  errorKey: string;
  message: string;
}

export interface ICallResult {
  data: any;
  errors: IErrorResult[];
  hasErrors: boolean;
}

export interface IResizeObserverSize {
  width: number;
  height?: number;
}

export class DrawerInfo {
  constructor(id: AppEnums.DrawerIds) {
    this.id = id;
  }

  id: AppEnums.DrawerIds = AppEnums.DrawerIds.NotSpecified;
  title: string = '';
  icon?: string | undefined;
  subtitle?: string | undefined;
  size: AppCoreEnums.RightSideDrawerSize = AppCoreEnums.RightSideDrawerSize.Medium2;
  saveBtnLabel?: string;
  saveBtnIcon?: string | undefined;
  saveBtnColor?: string;
  cancelBtnLabel?: string = 'core.cancel';
  hideSave: boolean = false;
  hideConfirmationMessageCancel?: boolean;
  isDrawerRendered?: boolean;
  ignoreChangesOnClose?: boolean | undefined;
  saveChangesMsgResourceId?: string | undefined;
  switchConfirmationButtons?: boolean | undefined;
  additionalBtnIcon?: string | undefined;
  additionalBtnLabel?: string | undefined;
  data?: any;
  drawerNumber?: number;
  disableScrollArea?: boolean | undefined;
  hideActions?: boolean | undefined;
  disableModal?: boolean | undefined;
}

export class BreadcrumbItem {
  constructor(label: string, route: string, routeLevel: number) {
    this.label = label;
    this.route = route;
    this.routeLevel = routeLevel;
  }
  label: string;
  route: string;
  icon?: string;
  routeLevel: number;
}

export interface ISelectItem {
  value: any;
  label: string;
  //extra: any;
  //key: any;
  isSelected?: boolean;
  meta?: string;
  icon: string;

  command?: () => void;
}

export interface ISelectListItem {
  value: any;
  title: string;
  headerTitle: string;
  description: string;
  data: any;
}

export interface IDynamicFilterValueOptions {
  label: string;
  key: string;
  options?: ISelectItem[];
  type: AppCoreEnums.FilterItemType;
  isSticky?: boolean | undefined;
}

export interface IDateRange {
  from: string | null;
  to: string | null;
  rangeOption: AppCoreEnums.DynamicFilterDateRangeOptionsEnum;
  rangeOptionLabelResourceId: string;
}

export interface IDynamicFilterItem {
  key: string;
  keyResourceId: string;
  value: string | string[] | IDateRange | number | undefined;
  valueResourceId: string | string[] | IDateRange | undefined;
  type: AppCoreEnums.FilterItemType | undefined;
  isDefault: boolean;
  isStickyInternal?: boolean | undefined;
}

export class AppTableColumnSelection {
  name: string;
  isColumnVisible?: boolean;

  constructor(name: string, isColumnVisible: boolean | undefined) {
    this.name = name;
    this.isColumnVisible = isColumnVisible == undefined ? true : isColumnVisible;
  }
}

export interface IAppTableColumnSelectionDrawerData {
  containerType: AppCoreEnums.ContainerType;
  //allColumns: IAppTableColumn<any>[];
  defaultVisibleColumns: string[];
}

export interface IResizeValue {
  height: number;
  width: number;
}

export interface IFacetFilterMoreTableRow {
  item1?: ISelectItem;
  item2?: ISelectItem;
  item3?: ISelectItem;
}

export interface IHttpRequestFormField {
  name: string;
  value: any;
}

export interface IFile {
  idFile?: string;
  fileName?: string;
  description: string;
  blobFilePath?: string;
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
  isDeleted?: boolean;
}

export interface IFileDownload {
  fileName: string;
  contentType: string;
  fileContent: string;
}

export interface FacetInterface {
  name: string;
  items: FacetItemInterface[];
}

export interface FacetItemInterface {
  label: string;
  count?: number | null;
  isSelected: boolean;
}

export interface ImportMetaEnvApp {
  VITE_API_URL: string;
}

export interface IToastMessage {
  title: string;
  message: string;
  severity: 'success' | 'info' | 'warn' | 'error';
  translate?: boolean;
}

export class TableBasedContainerData {
  constructor() {
    this.filters = [];
    this.searchBy = '';
    this.routeParams = {};
    this.selectedRows = [];
    this.selectedKeys = [];
    this.dynamicFilters = [];
    this.deleteRequestCount = 0;
    this.refreshRequestCount = 0;
    this.dynamicFilterOptions = [];
    this.pagination = new Pagination();
    this.options = new TableBasedContainerOptions([], [], '');
    this.containerType = AppCoreEnums.ContainerType.NotSpecifiedContainer;
  }

  reset() {
    this.searchBy = '';
    this.filters = [];
    this.dynamicFilters = [];
    this.resetSelectedData();
    //this.options.columns.length = 0;
    //this.options.rows.length = 0; // = new TableBasedContainerOptions();
  }

  resetSelectedData() {
    this.selectedRows = [];
    this.selectedKeys = [];
  }

  searchBy: string;
  selectedKeys: number[];
  filters: IFilterItem[];
  pagination: Pagination;
  selectedRows: any[] | any;
  deleteRequestCount: number;
  refreshRequestCount: number;
  options: TableBasedContainerOptions;
  dynamicFilters: IDynamicFilterItem[];
  containerType: AppCoreEnums.ContainerType;
  dynamicFilterOptions: IDynamicFilterValueOptions[];
  routeParams: Partial<Record<'id' | 'id2' | 'id3', string>>;
}

export class CollapsibleContainerData extends TableBasedContainerData {
  constructor() {
    super();

    this.title = '';
    this.isLeftPanelInMiniMode = false;
    this.containerType = AppCoreEnums.ContainerType.CollapsibleContainer;
  }

  title: string;
  isLeftPanelInMiniMode: boolean;
}

export class CollapsibleTableContainerData extends TableBasedContainerData {
  constructor() {
    super();

    this.title = '';
    this.detailsTitle = '';
    this.isLeftPanelCollapsed = false;
    this.isLeftPanelInMiniMode = false;
    this.isLeftPanelInMiniModeExplicitRequestedByUser = false;
    this.showFilters = true;
    this.containerType = AppCoreEnums.ContainerType.CollapsibleTableContainer;
  }

  title: string;
  detailsTitle: string;
  isLeftPanelCollapsed: boolean;
  isLeftPanelInMiniMode: boolean;
  isLeftPanelInMiniModeExplicitRequestedByUser: boolean;
  showFilters: boolean;
}

export class CollapsibleTableInnerContainerData extends TableBasedContainerData {
  constructor() {
    super();

    this.title = '';
    this.detailsTitle = '';
    this.isLeftPanelCollapsed = false;
    this.isLeftPanelInMiniMode = false;
    this.isLeftPanelInMiniModeExplicitRequestedByUser = false;
    this.containerType = AppCoreEnums.ContainerType.CollapsibleTableInnerContainer;
  }

  title: string;
  detailsTitle: string;
  isLeftPanelCollapsed: boolean;
  isLeftPanelInMiniMode: boolean;
  isLeftPanelInMiniModeExplicitRequestedByUser: boolean;
}

export class TabsContainerData {
  constructor() {
    this.isLeftPanelInMiniMode = false;
  }
  isLeftPanelInMiniMode: boolean;
  lastActiveRouteName: string | undefined;
}

export class TableContainerData extends TableBasedContainerData {
  constructor() {
    super();
    this.containerType = AppCoreEnums.ContainerType.TableContainer;
  }
}

export class TableInnerContainerData extends TableBasedContainerData {
  constructor() {
    super();
    this.containerType = AppCoreEnums.ContainerType.TableInnerContainer;
  }
}

export class DrawerContainerData extends TableBasedContainerData {
  constructor() {
    super();
    this.shelfData = {};
    this.isDirty = false;
    this.afterClosedCnt = 0;
    this.submitRequestCnt = 0;
    this.containerType = AppCoreEnums.ContainerType.DrawerContainer;
  }
  shelfData: any;
  isDirty: boolean;
  afterClosedCnt: number;
  submitRequestCnt: number;
  resolveDrawerParams: ResolveDrawerParams | undefined;
}

export class ListContainerData extends TableBasedContainerData {
  constructor() {
    super();
    this.containerType = AppCoreEnums.ContainerType.ListContainer;
  }
}

export class FormContainerData {
  constructor() {
    this.title = '';
    this.isDirty = false;
    this.saveRequestCnt = 0;
    this.deleteRequestCnt = 0;
    this.refreshRequestCnt = 0;
    this.submitRequestCnt = 0;
    this.validationFailedCnt = 0;
    this.undoChangesRequestCnt = 0;
    this.isFormModelDeleted = false;
  }
  isDirty: boolean;
  saveRequestCnt: number;
  refreshRequestCnt: number;
  deleteRequestCnt: number;
  submitRequestCnt: number;
  title: string | undefined;
  validationFailedCnt: number;
  undoChangesRequestCnt: number;
  isFormModelDeleted: boolean;
  savePromise: undefined | IFormSavePromise;
  requestSave(promise: undefined | IFormSavePromise = undefined) {
    this.saveRequestCnt++;
    this.savePromise = promise;
  }
}

export interface AppMainMenuItem extends MenuItem {
  hasChildActiveRoute?: boolean | undefined;
  items?: MenuItem[] | undefined;
  badgeValue?: number | undefined;
}

export interface IAppToolbarItemBase {
  id: string;
  width?: number | undefined;
}

export interface IAppToolbarButtonItem {
  value: string;
  label: string;
  icon?: string | undefined;
  iconImg?: string | undefined;
}

export interface IAppToolbarItem extends IAppToolbarItemBase {
  label: string | undefined;
  itemType?: AppCoreEnums.ToolbarItemTypes | undefined;
  icon?: string | undefined;
  iconImg?: string | undefined;
  iconImgDark?: string | undefined;
  click: ((...args: any[]) => any) | undefined;
  isHidden?: boolean | undefined;
  disabled?: boolean | undefined;
  isPrimary?: boolean | undefined;
  tooltip?: string | undefined;
  modelValue?: boolean | undefined;
  buttonMenuItems: IAppToolbarButtonItem[];
  getButtonMenuItemsInitialSelectedValue?: () => string | undefined;
  severity: 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'help' | 'danger' | 'contrast' | undefined;
  permission?: string | undefined;
}

export class SizeInfo {
  constructor() {
    this.windowHeight = 0;
    this.breadcrumbsHeight = 0;
    this.tableContainerToolbarHeight = 0;
    this.tableInnerContainerToolbarHeight = 0;
    this.tableContainerFiltersHeight = 0;
    this.tableInnerContainerFiltersHeight = 0;
    this.collapsibleTableContainerToolbarHeight = 0;
    this.collapsibleTableContainerFiltersHeight = 0;
    this.collapsibleTableContainerLeftPanelWidth = 0;
    this.collapsibleContainerLeftPanelWidth = 0;
    this.collapsibleContainerRightPanelTitleHeight = 0;
    this.collapsibleContainerToolbarHeight = 0;
    this.formContainerToolbarHeight = 0;
    this.drawerActionsHeight = 0;
    this.drawerHeaderHeight = 0;
    this.mainMenuWidth = 0;
    this.tabsContainerHeight = 0;
    this.tabsContainerTitleHeight = 0;
    this.chatGptToolbarHeight = 0;
    this.formContainerHeight = 0;
  }
  windowHeight: number;
  breadcrumbsHeight: number;
  tableContainerToolbarHeight: number;
  tableInnerContainerToolbarHeight: number;
  tableContainerFiltersHeight: number;
  tableInnerContainerFiltersHeight: number;
  collapsibleTableContainerToolbarHeight: number;
  collapsibleTableContainerFiltersHeight: number;
  collapsibleTableContainerLeftPanelWidth: number;
  collapsibleContainerLeftPanelWidth: number;
  collapsibleContainerRightPanelTitleHeight: number;
  collapsibleContainerToolbarHeight: number;
  formContainerToolbarHeight: number;
  drawerHeaderHeight: number;
  drawerActionsHeight: number;
  mainMenuWidth: number;
  tabsContainerHeight: number;
  tabsContainerTitleHeight: number;
  chatGptToolbarHeight: number;
  formContainerHeight: number;
}

export class ConfirmationDialogData {
  constructor() {
    this.title = '';
    this.message = '';
    this.showCancel = true;
    this.isVisible = false;
    this.rejectFn = () => {};
    this.showNegative = false;
    this.resolveFn = () => {};
  }
  title: string;
  message: string;
  isVisible: boolean;
  rejectFn: Function;
  resolveFn: Function;
  showCancel: boolean;
  showNegative: boolean;
  negativeLabel: string = 'core.no';
  positiveLabel: string = 'core.yes';
  isDeleteConfirmation?: boolean = false;
}

export interface ClientSidePaginationInterface {
  page: number;
  rowsPerPage: number;
  sortBy?: string;
  descending: boolean;
}

export class Pagination {
  page = 1;
  rowsPerPage?: number | undefined = 6;
  rowsNumber?: number | undefined = undefined;
  sortBy?: string | undefined = undefined;
  descending?: boolean = false;
  skipActualPage?: boolean = false;

  constructor(rowsPerPage: number = 6, page: number = 1) {
    this.page = page;
    this.rowsPerPage = rowsPerPage;
    this.rowsNumber = undefined;
    this.sortBy = undefined;
    this.descending = false;
    this.skipActualPage = false;
  }
}

const defaultPagination = <Pagination>{
  page: 1,
  rowsNumber: 0,
  sortBy: '',
  rowsPerPage: 6,
  descending: false,
};

export { defaultPagination };

export interface IFilterItem {
  fieldName: string;
  values: string[];
  filterType: AppCoreEnums.FilterItemType;
}

export interface IFormToolbarOptions {
  showRefresh?: boolean;
  showDelete?: boolean;
  showSave?: boolean;
  hideToolbar?: boolean;
  isClosable?: boolean; // used for cases when form container is not inside tabs container
  sideMenuFormTitle?: string;
  sideMenuFormSubTitle?: string;
}

export class TableBasedContainerToolbarOptions {
  showAddNew = true;
  showAddNewLabel = true;
  showRefresh = true;
  showRefreshLabel = true;
  showDelete = true;
  canDelete = true;
  showFilters = true;
  showClose = false;
  showColumnsSelection = true;
  isDeleteEnabled = true;
  items: IAppToolbarItem[] = [];
  itemsPosition: 'left' | 'right' = 'left';
  newLabel: string = 'core.new';
  disableAddNew? = false
}

export class TableBasedContainerOptions {
  constructor(columns: IColumn[] = [], rows: any[] = [], modelClassName: string) {
    columns.forEach((x) => {
      if (x.formatAsDateTime) {
        x.class = 'max-w-[150px] !w-[150px]';
      }
      if (x.formatAsAvatar) {
        x.class = '!max-w-[300px] w-[300px]';
      }

      if (x.field == 'description') {
        x.class = 'max-w-[250px]';
      }
    });

    const modelName = modelClassName?.length
      ? this.handlePostFixModel(modelClassName.charAt(0).toLowerCase() + modelClassName.slice(1))
      : '';

    this.rows = rows;
    this.columns = columns;
    this.modelClassName = modelClassName;
    this.idPropertyName = 'id';
    this.selectionMode = 'multiple';
    this.title = ''; // `${modelName}.title`;
    this.toolbarOptions = new TableBasedContainerToolbarOptions();
    this.toolbarOptions.newLabel = `${modelName}.new`;
    this.notifications = new TableBasedContainerNotificationMsgs(modelName);
    this.useCustomLeftPanel = () => false;
    this.customLeftPanelTitle = undefined;
    this.showSearch = true;
    this.showToolbar = true;
    this.isRowExpandable = false;
    this.expandRowFieldName = '';
    this.useMaximizedCustomLeftPanel = () => false;
    this.hideTable = () => false;
    this.enableContainerRefreshOutsideTabsContainer = false;
    this.useClientSidePagination = false;
  }

  rows: any[];
  title: string;
  columns: IColumn[];
  modelClassName: string;
  idPropertyName: string;
  selectionMode: 'single' | 'multiple' | undefined;
  toolbarOptions: TableBasedContainerToolbarOptions;
  notifications: TableBasedContainerNotificationMsgs;
  useCustomLeftPanel: () => boolean;
  useMaximizedCustomLeftPanel: () => boolean;
  customLeftPanelTitle: string | undefined;
  showSearch: boolean;
  showToolbar: boolean;
  isRowExpandable: boolean;
  expandRowFieldName: string;
  hideTable: () => boolean;
  enableContainerRefreshOutsideTabsContainer: boolean;
  useClientSidePagination: boolean;

  getTitle() {
    return this.title;
  }

  handlePostFixModel(modelName: string) {
    var postFix = 's';
    if (modelName.endsWith('y')) {
      postFix = 'ies';
      modelName = modelName.substring(0, modelName.length - 1);
    } else if (modelName.endsWith('x')) {
      postFix = 'es';
    }
    modelName = modelName + postFix;
    return modelName;
  }
}

export interface IPagedList<T> {
  dataList: T[];
  totalRecords: number;
  pageNumber: number;
  pageSize: number;
}

export interface FiltersBaseInterface {
  searchBy?: string | undefined;
  showDeleted?: boolean;
  pagination?: Pagination;
  filterItems: IFilterItem[];
}

export interface IColumnChipOptions {
  borderColor: string;
  textColor: string;
  bgColor: string;
}

export interface IColumn extends ColumnProps {
  detailsRoute: string | undefined;
  detailsRouteParamField: string;
  formatField?: (row: any) => any;
  getIcon?: (row: any) => string | undefined;
  getIconImg?: (row: any) => string | undefined;
  getClass?: (row: any) => string | undefined;
  getTooltip?: (row: any) => string | undefined;
  getChipOptions?: (row: any) => IColumnChipOptions | undefined;
  formatAsDateTime?: boolean;
  formatAsAvatar?: boolean; // TODO: create a fieldType enum if many formats needed
  formatAsActions?: boolean;
  formatAsImage?: boolean;
  useCustomTemplate?: boolean;
  useCustomTemplate2?: boolean;
  useCustomTemplate3?: boolean;
  isVisible?: boolean;
  position?: number;
  filterKey?: string;
}

export interface TableDependecy {
  id: number;
  entityName: string;
  entityId: number;
  entityType: string;
  dependencyId: number;
  dependenciesName: string;
}

export interface DeleteResult {
  isSucces: boolean;
  tableDependecies: TableDependecy[];
}

export interface ConversionResult {
  result: string;
  error: string;
  title: string;
}

export class BaseModel {
  createdAt: string = new Date().toISOString();
  createdBy: number = 0;
  createdByName: string = '';
  updatedAt?: string = '';
  updatedBy?: number = 0;
  updatedByName?: string = '';

  entityStatus: EntityStatuses = EntityStatuses.Active;
}

export interface AppToastMessageOptions extends ToastMessageOptions {
  type: AppCoreEnums.ToastNotificationType;
  customAction: ToastNotificationAction | undefined;
  summary2: string | undefined;
  detail2: string | undefined;
  feedbackValue: AppCoreEnums.UserFeelingFeedback | null;
}

export class ToastNotificationAction {
  label: string = '';
  type: AppCoreEnums.ToastNotificationType = AppCoreEnums.ToastNotificationType.Success;
  route: RouteLocationRaw | undefined = undefined;
  callback: (() => void) | undefined;
}

export class FormNotificationMsgs {
  successfullySaveTitle?: string = '';
  successfullySaveMsg: string = '';
  navigateToDetailsMsg?: string = '';
}

export class TableBasedContainerNotificationMsgs {
  constructor(entityName: string | undefined) {
    if (entityName) {
      const entityNamePlural = entityName.endsWith('s') ? entityName : entityName + 's';
      this.successfullyDeleteTitle = `${entityNamePlural}.notifications.successfullyDeleteTitle`;
      this.successfullyDeleteMsg = `${entityNamePlural}.notifications.successfullyDeleteMsg`;

      this.successfullyRestoredTitle = `${entityNamePlural}.notifications.successfullyRestoredTitle`;
      this.successfullyRestoredMsg = `${entityNamePlural}.notifications.successfullyRestoredMsg`;
      this.deleteBlockedTitle = `${entityNamePlural}.notifications.deleteBlockedTitle`;
      this.deleteBlockedMsg = `${entityNamePlural}.notifications.deleteBlockedMsg`;
    }
  }
  successfullyDeleteTitle = '';
  successfullyDeleteMsg = '';

  successfullyRestoredTitle = '';
  successfullyRestoredMsg = '';

  deleteBlockedTitle = '';
  deleteBlockedMsg = '';
}

export class FormStepperConfig {
  constructor(steps: IFormStepperStep[]) {
    this.steps = steps;
    this.resetSteps = () => {
      this.steps.forEach((x) => (x.isValid = false));
    };
  }
  steps: IFormStepperStep[];
  resetSteps: () => void;
}

export interface IFormStepperStep {
  name: string;
  title: string;
  subtitle: string;
  isValid?: boolean | undefined;
  isOptional?: boolean;
}

export class DeletePayload {
  entityName = '';
  ids: number[] = [];
}

export class RestorePayload {
  entityName = '';
  ids: number[] = [];
}

export class TranslationItem {
  lang = '';
  value = '';
}

export interface IRadioOptions {
  key: any;
  name: string;
  value: string | any;
  label: string;
}

export interface IBaseModel {
  tsUTC: string;
}

export class WebSocketMessage {
  Type: AppEnums.WebSocketMessageTypes = AppEnums.WebSocketMessageTypes.NotSet;
  JsonContent: any;
}

export interface ISimpleItem {
  id: number;
  name: string;
  optionalName?: string;
  meta?: any;
}

export interface ISideMenuItem {
  name: string;
  icon: string;
  route: string;
  badgeValue?: Ref<number> | undefined;
}

export class FileUploadInfo {
  constructor() {
    this.file = {};
    this.type = '';
    this.path = '';
    this.fileId = -1;
    this.isUploading = false;
    this.uploadProgress = -1;
  }
  file: any;
  type: any;
  path: string;
  fileId: number;
  isUploading: boolean;
  uploadProgress: number;
}
